import { useState, useRef, useCallback } from "react";
import { handleFileUpload } from "../utils/fileUploadHandler";
import UserAuthStore from "../store/userStore";
import { MAX_FILE_COUNT } from "../constants/fileConstants";
import useFeedbackStore from "../store/useFeedbackStore";

/**
 * useInlineEditAttachments
 *
 * Lightweight attachment state for the inline AI editor (NebulaCanvas row 2).
 * Manages its own local files array — never touches FileStore so uploads here
 * never bleed into the main chat prompt or new_chat_files bucket.
 *
 * File shape (each entry):
 *   { name, size, file__path, downloadURL, progress, abortController }
 *
 * @param {string} chatId - Current chat_id (used for upload path)
 * @returns {{
 *   files: Array,
 *   fileInputRef: React.RefObject,
 *   openFilePicker: () => void,
 *   onFileInputChange: (e) => void,
 *   removeFile: (file) => void,
 *   clearFiles: () => void,
 *   assetIds: string[],   — canonical IDs of fully uploaded files
 * }}
 */
export default function useInlineEditAttachments(chatId) {
  const [files, setFiles] = useState([]);
  const fileInputRef = useRef(null);

  const openFilePicker = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const onFileInputChange = useCallback(
    (e) => {
      const { user, orgId } = UserAuthStore.getState();
      if (files.length >= MAX_FILE_COUNT) {
        useFeedbackStore
          .getState()
          .showFeedback(`Cannot attach more than ${MAX_FILE_COUNT} files.`, "error");
        return;
      }
      // Delegate to shared upload handler with a local setFiles override so
      // the resulting file objects land in our local state, not FileStore.
      handleFileUpload(
        e,
        chatId,
        fileInputRef,
        setFiles,
        files,
        user,
        orgId,
        1,        // messageLength > 0 — ensures uploadFile path is taken (never new_chat_files)
        false,    // isNewChat = false — same reason
        null,     // space = null (not in Spectra context)
      );
      // Reset input so the same file can be re-selected after removal
      if (fileInputRef.current) fileInputRef.current.value = "";
    },
    [chatId, files],
  );

  const handlePaste = useCallback(
    (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      const pastedFiles = [];
      for (const item of items) {
        if (item.kind === "file") {
          const file = item.getAsFile();
          if (file) pastedFiles.push(file);
        }
      }
      if (pastedFiles.length === 0) return;
      const { user, orgId } = UserAuthStore.getState();
      if (files.length >= MAX_FILE_COUNT) {
        useFeedbackStore
          .getState()
          .showFeedback(`Cannot attach more than ${MAX_FILE_COUNT} files.`, "error");
        return;
      }
      const dt = new DataTransfer();
      pastedFiles.forEach((f) => dt.items.add(f));
      if (fileInputRef.current) fileInputRef.current.files = dt.files;
      handleFileUpload(
        e,
        chatId,
        fileInputRef,
        setFiles,
        files,
        user,
        orgId,
        1,
        false,
        null,
      );
    },
    [chatId, files],
  );

  const removeFile = useCallback((file) => {
    file?.abortController?.abort();
    setFiles((prev) => prev.filter((f) => f.file__path !== file.file__path));
  }, []);

  const clearFiles = useCallback(() => {
    files.forEach((f) => f?.abortController?.abort());
    setFiles([]);
  }, [files]);

  const assetIds = files
    .filter((file) => file?.downloadURL && file?.asset_id)
    .map((file) => file.asset_id);

  return {
    files,
    fileInputRef,
    openFilePicker,
    onFileInputChange,
    handlePaste,
    removeFile,
    clearFiles,
    assetIds,
  };
}
